import { createApp } from '@backstage/frontend-defaults';
import { createFrontendModule } from '@backstage/frontend-plugin-api';
import { SignInPageBlueprint, ThemeBlueprint } from '@backstage/plugin-app-react';
import { SignInPage } from '@backstage/core-components';
import { microsoftAuthApiRef } from '@backstage/core-plugin-api';
import { UnifiedThemeProvider } from '@backstage/theme';
import LightIcon from '@material-ui/icons/WbSunny';
import DarkIcon from '@material-ui/icons/Brightness2';
import { pyranaLightTheme, pyranaDarkTheme } from './themes/pyranaTheme';
import catalogPlugin from '@backstage/plugin-catalog/alpha';
import scaffolderPlugin from '@backstage/plugin-scaffolder/alpha';
import searchPlugin from '@backstage/plugin-search/alpha';
import techdocsPlugin from '@backstage/plugin-techdocs/alpha';
import orgPlugin from '@backstage/plugin-org/alpha';
import apiDocsPlugin from '@backstage/plugin-api-docs/alpha';
import catalogImportPlugin from '@backstage/plugin-catalog-import/alpha';
import userSettingsPlugin from '@backstage/plugin-user-settings/alpha';
import catalogGraphPlugin from '@backstage/plugin-catalog-graph/alpha';
import techRadarPlugin from '@backstage-community/plugin-tech-radar/alpha';
import { navModule } from './modules/nav';
import { homePlugin } from './modules/home';
import { kubernetesDashboardPlugin } from './modules/kubernetes-dashboard';
import { techRadarModule } from './modules/tech-radar';

// Native Microsoft Entra ID sign-in. The backend (authMicrosoftZeroth module)
// enforces that only @zeroth.technology accounts may complete sign-in.
// No `name` is given so this OVERRIDES the app plugin's default (guest)
// sign-in page rather than registering a conflicting second one.
const microsoftSignInPage = SignInPageBlueprint.make({
  params: {
    loader: async () => props =>
      (
        <SignInPage
          {...props}
          provider={{
            id: 'microsoft-auth-provider',
            title: 'Microsoft',
            message: 'Sign in with your zeroth.technology account',
            apiRef: microsoftAuthApiRef,
          }}
        />
      ),
  },
});

// Pyrana-branded themes. Registered with the same names ("light"/"dark") as the
// app plugin's built-ins so they OVERRIDE them (rather than adding a 3rd/4th
// entry to the theme picker). Must live inside the `app` frontend module — the
// same registration path the sign-in override above uses.
const pyranaLight = ThemeBlueprint.make({
  name: 'light',
  params: {
    theme: {
      id: 'light',
      title: 'Pyrana Light',
      variant: 'light',
      icon: <LightIcon />,
      Provider: ({ children }) => (
        <UnifiedThemeProvider theme={pyranaLightTheme} children={children} />
      ),
    },
  },
});

const pyranaDark = ThemeBlueprint.make({
  name: 'dark',
  params: {
    theme: {
      id: 'dark',
      title: 'Pyrana Dark',
      variant: 'dark',
      icon: <DarkIcon />,
      Provider: ({ children }) => (
        <UnifiedThemeProvider theme={pyranaDarkTheme} children={children} />
      ),
    },
  },
});

export default createApp({
  features: [
    createFrontendModule({
      pluginId: 'app',
      extensions: [microsoftSignInPage, pyranaLight, pyranaDark],
    }),
    catalogPlugin,
    scaffolderPlugin,
    searchPlugin,
    techdocsPlugin,
    orgPlugin,
    apiDocsPlugin,
    catalogImportPlugin,
    userSettingsPlugin,
    catalogGraphPlugin,
    techRadarPlugin,
    homePlugin,
    kubernetesDashboardPlugin,
    techRadarModule,
    navModule,
  ],
});
